import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { MyWeekItem, Residency } from '@salonomy/types';
import { Button, Card, IconButton, Spinner } from '@salonomy/ui';
import { useMyWeek, useResidencies } from '../../data/practitioner.js';
import { money } from '../../lib/format.js';
import {
  addDays,
  isoDateInZone,
  longDate,
  spanLabel,
  todayInZone,
  zonedDayStart,
} from '../../lib/time.js';
import {
  buildLocationColors,
  colorFor,
  groupByDay,
  placeLabel,
  type PlaceColor,
} from './locationColors.js';
import { SwitchToBookCard } from './SwitchToBookCard.js';
import './myweek.css';

/**
 * WS11 — "My week" UNIFIED DIARY (the flagship practitioner/identity-spanning surface; replaces the
 * design-gated scaffold). One day-grouped agenda across EVERY salon the practitioner works in plus their
 * own studio, colour-coded BY PLACE (one stable accent per salonId + the private studio), with a place
 * legend, per-item location pill and week navigation. A day-grouped agenda — NOT the per-chair 2D column
 * grid (those columns are per-chair-in-one-tenant) — is the correct cross-location shape, matching the
 * mobile owner "This week" screen.
 *
 * HONESTY (mirror data/practitioner.ts): every row is a SERVER figure from the read-side union. The ONLY
 * money rendered is MyWeekItem.priceQuoted via money(); nothing about the aggregate, money, VAT,
 * proration or availability is recomputed on the client.
 *
 * TIMEZONE: wall-clock is rendered in the ACTIVE salon's timezone (useSalon().timezone, passed as `tz`)
 * and the surface is LABELLED with that tz ("times shown in <salon> time"). Per-leg timezones are a
 * deferred API change — we do NOT silently per-locate each row.
 *
 * WRITE AFFORDANCE: there is NO cross-tenant write endpoint. Booking/editing in another place is modelled
 * as a one-tap "Switch into <salon> to book" (SwitchToBookCard) = auth.switchSalon then navigate('/') into
 * that salon's diary — the approved StartPracticeModal.switchIn() pattern. NEVER an in-place cross-tenant write.
 */

const WEEK_DAYS = 7;

export function MyWeek({
  tz,
  salonName,
  activeSalonId,
}: {
  tz: string;
  salonName?: string;
  activeSalonId?: string;
}) {
  // The visible week as a salon-zone start date; navigation moves it in 7-day steps. The window is
  // [from, to) with `to` EXCLUSIVE (the useMyWeek contract); we only choose the window — the union is
  // the server's. `weekStart` is a "YYYY-MM-DD" anchor (this week = today, in the salon zone).
  const [weekStart, setWeekStart] = useState(() => todayInZone(tz));

  // The fetch window must be aligned to SALON-LOCAL midnight, not UTC midnight — weekStart is a salon-zone
  // date, so a naive `${weekStart}T00:00:00Z` is skewed by the salon's UTC offset (it drops the salon-local
  // first hours of day 1 and pulls in items belonging to the day AFTER the displayed week). zonedDayStart
  // converts a salon date to the correct UTC instant exactly as the daily diary (zonedDayRange) and the
  // per-chair WeekStrip already do, so the union window matches the displayed/group-bucketed salon-zone days.
  const range = useMemo(
    () => ({
      from: zonedDayStart(weekStart, tz).toISOString(),
      to: zonedDayStart(addDays(weekStart, WEEK_DAYS), tz).toISOString(),
    }),
    [weekStart, tz],
  );

  const residencies = useResidencies();
  const week = useMyWeek(range);

  const today = todayInZone(tz);
  const weekEnd = addDays(weekStart, WEEK_DAYS - 1); // inclusive last day for the label
  const isThisWeek = today >= weekStart && today <= weekEnd;

  const items = week.data ?? [];

  // The place colour map is built over BOTH the residencies (so every place you work has a legend entry,
  // even with no appointments this week) and the week's items (a one-off leg still gets a stable colour).
  const colorMap = useMemo(() => {
    const activeResidencies = (residencies.data ?? []).filter((r) => r.active);
    const legs = [
      ...activeResidencies.map((r) => ({
        salonId: r.salonId,
        isPrivatePractice: r.isPrivatePractice,
      })),
      ...items.map((it) => ({ salonId: it.salonId, isPrivatePractice: it.isPrivatePractice })),
    ];
    return buildLocationColors(legs);
  }, [residencies.data, items]);

  // Day-grouped agenda in the active salon zone (pure helper; the projection binds tz here).
  const groups = useMemo(
    () => groupByDay(items, (iso) => isoDateInZone(new Date(iso), tz)),
    [items, tz],
  );

  // The legend rows: one stable entry per place, ordered studio-first then by name (a calm reading order).
  const legend = useMemo(
    () => legendRows(residencies.data ?? [], items, colorMap),
    [residencies.data, items, colorMap],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card>
        <div className="myweek-nav">
          <div>
            <div className="section-title">Your week</div>
            <div className="myweek-nav__range">
              {longDate(weekStart, tz)} — {longDate(weekEnd, tz)}
            </div>
            <div className="myweek-nav__tz">
              One diary across every place you work. Times shown in{' '}
              {salonName ? `${salonName} time` : 'salon time'}.
            </div>
          </div>
          <div className="myweek-nav__btns">
            {week.isFetching ? <Spinner size={16} /> : null}
            <IconButton
              label="Previous week"
              onClick={() => setWeekStart((d) => addDays(d, -WEEK_DAYS))}
            >
              ‹
            </IconButton>
            <Button
              size="sm"
              variant={isThisWeek ? 'soft' : 'outline'}
              onClick={() => setWeekStart(today)}
              disabled={isThisWeek}
            >
              This week
            </Button>
            <IconButton
              label="Next week"
              onClick={() => setWeekStart((d) => addDays(d, WEEK_DAYS))}
            >
              ›
            </IconButton>
          </div>
        </div>

        {legend.length > 0 ? (
          <div className="myweek-legend">
            {legend.map((l) => (
              <span key={l.salonId} className="myweek-legend__item" style={accentVars(l.color)}>
                <span className="myweek-legend__dot" />
                {l.name}
                {l.color.isPrivatePractice ? (
                  <span className="myweek-legend__studio">your studio</span>
                ) : null}
              </span>
            ))}
          </div>
        ) : null}
      </Card>

      <Card>
        {week.isLoading ? (
          <div className="myweek-state">Loading your week…</div>
        ) : week.isError ? (
          <div className="myweek-state">Could not load your week right now.</div>
        ) : groups.length === 0 ? (
          <div className="myweek-state">
            {isThisWeek
              ? 'No appointments across your salons this week.'
              : 'No appointments across your salons in this week.'}
          </div>
        ) : (
          <div className="myweek-days">
            {groups.map((g) => (
              <div key={g.date}>
                <div className="myweek-day__head">
                  <span
                    className={`myweek-day__date${g.date === today ? ' myweek-day__date--today' : ''}`}
                  >
                    {longDate(g.date, tz)}
                  </span>
                  <span className="myweek-day__count">
                    {g.items.length} {g.items.length === 1 ? 'appointment' : 'appointments'}
                  </span>
                </div>
                <div className="myweek-items">
                  {g.items.map((it) => (
                    <WeekRow key={it.appointmentId} item={it} tz={tz} colorMap={colorMap} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="section-title">Book or edit in another place</div>
        <div
          className="muted"
          style={{ fontSize: 13, marginTop: 4, marginBottom: 12, maxWidth: 560 }}
        >
          Each place keeps its own diary, clients and availability. To book or change an appointment
          somewhere you work, switch into that diary — one tap, then you’re in.
        </div>
        {legend.length === 0 ? (
          <div className="myweek-state" style={{ paddingTop: 0 }}>
            You don’t have any active salon residencies or a private practice yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {legend.map((l) => (
              <SwitchToBookCard
                key={l.salonId}
                salonId={l.salonId}
                salonName={l.rawName}
                isPrivatePractice={l.color.isPrivatePractice}
                activeSalonId={activeSalonId}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/** One appointment row, left-accented + location-pilled by its place. priceQuoted shown verbatim. */
function WeekRow({
  item,
  tz,
  colorMap,
}: {
  item: MyWeekItem;
  tz: string;
  colorMap: Map<string, PlaceColor>;
}) {
  const accent = colorFor(colorMap, item.salonId);
  return (
    <div className="myweek-item" style={accentVarsFromAccent(accent)}>
      <span className="myweek-item__time">{spanLabel(item.startAt, item.endAt, tz)}</span>
      <div className="myweek-item__body">
        <div className="myweek-item__client">{item.clientDisplayName || 'Walk-in'}</div>
        <div className="myweek-item__meta">
          <span className="myweek-pill">
            <span className="myweek-pill__dot" />
            {placeLabel(item.salonName, item.isPrivatePractice)}
          </span>
        </div>
      </div>
      <span className="myweek-item__price">{money(item.priceQuoted)}</span>
    </div>
  );
}

interface LegendRow {
  salonId: string;
  /** The display label (studio suffixed "(your studio)"). */
  name: string;
  /** The raw salon name (for the switch CTA, which appends its own copy). */
  rawName: string;
  color: PlaceColor;
}

/**
 * One legend row per distinct place, drawn from the residencies (every place you work) plus any week item
 * whose place isn't a current residency (a leg that still appears in the union). Studio-first, then by
 * name — a stable, calm reading order that mirrors the colour map's determinism.
 */
function legendRows(
  residencies: Residency[],
  items: MyWeekItem[],
  colorMap: Map<string, PlaceColor>,
): LegendRow[] {
  const names = new Map<string, { rawName: string; isPrivatePractice: boolean }>();
  for (const r of residencies) {
    if (r.active && !names.has(r.salonId)) {
      names.set(r.salonId, { rawName: r.salonName, isPrivatePractice: r.isPrivatePractice });
    }
  }
  for (const it of items) {
    if (!names.has(it.salonId)) {
      names.set(it.salonId, { rawName: it.salonName, isPrivatePractice: it.isPrivatePractice });
    }
  }
  return [...names.entries()]
    .map(([salonId, { rawName, isPrivatePractice }]) => ({
      salonId,
      rawName,
      name: placeLabel(rawName, isPrivatePractice),
      color: colorMap.get(salonId) ?? {
        salonId,
        accent: colorFor(colorMap, salonId),
        isPrivatePractice,
      },
    }))
    .sort(
      (a, b) =>
        Number(b.color.isPrivatePractice) - Number(a.color.isPrivatePractice) ||
        a.rawName.localeCompare(b.rawName),
    );
}

/** The custom properties that drive a place's soft tint + accent on the CSS (palette stays in accents.ts). */
function accentVars(color: PlaceColor): CSSProperties {
  return accentVarsFromAccent(color.accent);
}
function accentVarsFromAccent(accent: { accent: string; soft: string }): CSSProperties {
  return {
    ['--myweek-accent' as string]: accent.accent,
    ['--myweek-soft' as string]: accent.soft,
  };
}
